import React, {createContext, useCallback, useContext, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {Alert} from '@forecast-it/design-system';
import {hasPermission} from '../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../Permissions';
import {type AiCreditUsage, formatCreditResetDate, isAiCreditsExhausted, useAiCreditUsage} from './aiCreditUsage';
import {BannerWrapper} from './BannerWrapper';

export type CreditThreshold = '80' | '100';

const DISMISSED_PREFIX = 'aiCreditsBannerDismissed';

/**
 * Which credit notice applies. The spent-allowance notice only when the gateway is actually
 * refusing requests; while it is not, an account over 100% sees the 80% notice instead.
 */
export const creditThreshold = (usage: AiCreditUsage | null): CreditThreshold | null => {
	if (!usage || usage.unlimited) return null;
	if (isAiCreditsExhausted(usage)) return '100';
	if (usage.percentUsed >= 80) return '80';
	return null;
};

export const creditDismissalKey = (companyId: string, period: string, threshold: CreditThreshold): string =>
	`${DISMISSED_PREFIX}:${companyId}:${period}:${threshold}`;

const readCreditDismissed = (key: string): boolean => {
	try {
		return localStorage.getItem(key) === 'true';
	} catch {
		return false;
	}
};

const writeCreditDismissed = (companyId: string, period: string, key: string): void => {
	try {
		// Drop this company's records for earlier periods; they can never match again.
		const companyPrefix = `${DISMISSED_PREFIX}:${companyId}:`;
		const stale: string[] = [];
		for (let i = 0; i < localStorage.length; i++) {
			const storedKey = localStorage.key(i);
			if (storedKey && storedKey.startsWith(companyPrefix) && !storedKey.startsWith(`${companyPrefix}${period}:`)) {
				stale.push(storedKey);
			}
		}
		stale.forEach(storedKey => localStorage.removeItem(storedKey));
		localStorage.setItem(key, 'true');
	} catch {
		// Losing the record only costs a repeat of the banner.
	}
};

type CreditBannerContextValue = {
	usage: AiCreditUsage | null;
	threshold: CreditThreshold | null;
	isAdmin: boolean;
	dismissed: boolean;
	onDismiss: () => void;
	suppressed: boolean;
};

const CreditBannerContext = createContext<CreditBannerContextValue | null>(null);

type AiCreditBannerProviderProps = {
	companyId?: string | null;
	suppressed: boolean;
	children: React.ReactNode;
};

/**
 * Holds the credit balance and the viewer's dismissal for every AiCreditBanner below it.
 * suppressed stops the fetch and hides the banners, for when AI is switched off entirely.
 * Without a companyId, a dismissal lasts only until the page reloads.
 */
export const AiCreditBannerProvider = ({companyId, suppressed, children}: AiCreditBannerProviderProps): React.ReactElement => {
	const isAdmin = hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS);
	// Only admins see a credit banner, so nobody else needs the balance.
	const usage = useAiCreditUsage(!suppressed && isAdmin);
	const threshold = creditThreshold(usage);
	const period = usage ? usage.period : null;

	const storageKey = companyId && period && threshold ? creditDismissalKey(companyId, period, threshold) : null;
	const sessionKey = period && threshold ? `${companyId ?? ''}:${period}:${threshold}` : null;

	const [dismissedSessionKey, setDismissedSessionKey] = useState<string | null>(null);
	const storedDismissed = useMemo(() => (storageKey ? readCreditDismissed(storageKey) : false), [storageKey]);
	const dismissed = storedDismissed || (sessionKey !== null && dismissedSessionKey === sessionKey);

	const onDismiss = useCallback(() => {
		if (companyId && period && storageKey) writeCreditDismissed(companyId, period, storageKey);
		setDismissedSessionKey(sessionKey);
	}, [companyId, period, storageKey, sessionKey]);

	const contextValue = useMemo(
		() => ({
			usage: suppressed ? null : usage,
			threshold: suppressed ? null : threshold,
			isAdmin,
			dismissed,
			onDismiss,
			suppressed,
		}),
		[suppressed, usage, threshold, isAdmin, dismissed, onDismiss]
	);

	return <CreditBannerContext.Provider value={contextValue}>{children}</CreditBannerContext.Provider>;
};

/**
 * Whether AI credit notices are switched off because AI itself is. False outside a provider.
 */
export const useAiCreditsSuppressed = (): boolean => useContext(CreditBannerContext)?.suppressed ?? false;

/**
 * The dismissible credit banner for account admins. Everyone else learns the allowance is spent
 * from the AI chat's refusal. Renders nothing for a non-admin, below 80%, when the balance is
 * unknown, after a dismissal, or outside an AiCreditBannerProvider.
 */
export const AiCreditBanner = (): React.ReactElement | null => {
	const ctx = useContext(CreditBannerContext);
	const {formatMessage} = useIntl();
	if (!ctx || !ctx.isAdmin || ctx.dismissed || !ctx.usage || !ctx.threshold) return null;

	const date = formatCreditResetDate(ctx.usage.periodEnd);
	if (!date) return null;

	const spent = ctx.threshold === '100';
	// The warning names the real figure: an unenforced account past 100% still sees this notice.
	const percent = ctx.usage.percentUsed;

	const text = formatMessage(
		{id: spent ? 'gen_ai.credits.banner.admin_100' : 'gen_ai.credits.banner.admin_80'},
		{date, percent}
	);
	return (
		<BannerWrapper data-cy={spent ? 'ai-credits-banner-100' : 'ai-credits-banner-80'}>
			<Alert type={spent ? Alert.TYPE.WARNING : Alert.TYPE.INFORMATION} text={text} onDismiss={ctx.onDismiss} />
		</BannerWrapper>
	);
};
